import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { navItems } from "../data";
import PulseButton from "./PulseButton";

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track current path for highlighting active menu items
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  // Helper function to check if menu item is active
  const isActiveMenuItem = (href: string) => {
    if (href === "#home" || href === "/") {
      return currentPath === "/" || currentPath === "";
    }
    return (
      currentPath === href || currentPath.startsWith(href.replace("#", "/"))
    );
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const getHref = (href: string) => {
    if (href.startsWith("#")) {
      return "/" + href;
    }
    return href;
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    // If user clicked with Ctrl, Cmd, Shift, Alt, or middle click, let the browser default behavior handle it (open in new tab/window)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
      return;
    }

    setActiveDropdown(null); // Close dropdown after clicking a link
    setMobileMenuOpen(false); // Close mobile menu after clicking a link

    if (href.startsWith("#")) {
      const pathname = window.location.pathname;
      if (pathname === "/" || pathname === "") {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          window.history.pushState(null, "", href);
        }
      } else {
        e.preventDefault();
        window.location.href = "/" + href;
      }
    } else if (href.startsWith("/")) {
      const currentPath = window.location.pathname.replace(/\/$/, "");
      const targetPath = href.replace(/\/$/, "");
      if (currentPath === targetPath) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const handleMouseEnter = (label: string) => {
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 20 }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-2 sm:py-3"
          : "bg-white/90 backdrop-blur-sm shadow-md py-3 sm:py-5"
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-5 flex justify-between items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center"
        >
          <a
            href="/"
            onClick={(e) => handleNavClick(e, "/")}
            className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-1 sm:gap-2"
          >
            <img
              src="/logo.png"
              alt="MoizWP Logo"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
              onError={(e) => {
                // Fallback to text if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = "inline-block";
              }}
            />
            <span
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-md text-sm sm:text-base hidden"
              style={{ display: "none" }}
            >
              DC
            </span>
            <span className="hidden sm:inline">MoizWP</span>
            <span className="sm:hidden">MW</span>
          </a>
        </motion.div>

        <ul className="hidden lg:flex space-x-4 xl:space-x-8">
          {navItems.map((item) => (
            <li key={item.id} className="relative">
              {item.submenu ? (
                <div
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                  className="relative"
                >
                  <motion.a
                    href={getHref(item.href)}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="text-gray-700 font-medium hover:text-indigo-500 transition-colors duration-300 relative group flex items-center gap-1 cursor-pointer"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item.label}
                    <span className="text-xs">▼</span>
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-300 group-hover:w-full"></span>
                  </motion.a>

                  <AnimatePresence>
                    {activeDropdown === item.label && (
                      <motion.div
                        className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-4 ${
                          item.submenu && item.submenu.length > 10
                            ? "w-[900px] xl:w-[1000px]"
                            : item.submenu && item.submenu.length > 5
                              ? "w-[640px]"
                              : "w-80"
                        }`}
                        initial={{ opacity: 0, y: -10, x: "-50%" }}
                        animate={{ opacity: 1, y: 0, x: "-50%" }}
                        exit={{ opacity: 0, y: -10, x: "-50%" }}
                        transition={{ duration: 0.2 }}
                      >
                        <div
                          className={`grid gap-1 ${
                            item.submenu && item.submenu.length > 10
                              ? "grid-cols-3"
                              : item.submenu && item.submenu.length > 5
                                ? "grid-cols-2"
                                : "grid-cols-1"
                          }`}
                        >
                          {item.submenu?.map((subItem) => (
                            <motion.a
                              key={subItem.id}
                              href={getHref(subItem.href)}
                              className="text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-200 group block cursor-pointer"
                              onClick={(e) => handleNavClick(e, subItem.href)}
                              whileHover={{ x: 5 }}
                            >
                              <div className="font-medium text-gray-800 group-hover:text-indigo-500 transition-colors duration-200">
                                {subItem.label}
                              </div>
                              {subItem.description && (
                                <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                                  {subItem.description}
                                </div>
                              )}
                            </motion.a>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.a
                  href={getHref(item.href)}
                  className="text-gray-700 font-medium hover:text-indigo-500 transition-colors duration-300 relative group cursor-pointer inline-block"
                  onClick={(e) => handleNavClick(e, item.href)}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-300 group-hover:w-full"></span>
                </motion.a>
              )}
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          {/* Desktop CTA Button */}
          <PulseButton
            href="/contact"
            onClick={(e) => handleNavClick(e as any, "/contact")}
            variant="rounded"
            size="md"
            className="hidden sm:flex"
          >
            <span className="hidden md:inline">Get a Quote</span>
            <span className="md:hidden">Quote</span>
          </PulseButton>

          {/* Mobile Hamburger Menu */}
          <motion.button
            className="lg:hidden flex flex-col justify-center items-center w-8 h-8 focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
          >
            <motion.div
              className={`w-6 h-0.5 bg-gray-700 transition-all duration-300 ${
                mobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
              }`}
            />
            <motion.div
              className={`w-6 h-0.5 bg-gray-700 transition-all duration-300 my-1 ${
                mobileMenuOpen ? "opacity-0" : ""
              }`}
            />
            <motion.div
              className={`w-6 h-0.5 bg-gray-700 transition-all duration-300 ${
                mobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
              }`}
            />
          </motion.button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-[9998] lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Mobile Menu Panel */}
            <motion.div
              className="fixed top-0 right-0 h-screen w-80 bg-white shadow-xl z-[9999] lg:hidden border-l border-gray-200 flex flex-col max-w-[85vw] sm:max-w-[70vw]"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{
                backgroundColor: "#ffffff",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
                opacity: 1,
                background: "#ffffff",
                height: "100vh",
                top: 0,
              }}
            >
              {/* Mobile Menu Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
                <a
                  href="/"
                  onClick={(e) => handleNavClick(e, "/")}
                  className="flex items-center"
                >
                  <img
                    src="/logo.png"
                    alt="MoizWP Logo"
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      // Fallback to text if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = "inline-block";
                    }}
                  />
                  <span
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-1 rounded-md text-sm font-bold hidden"
                    style={{ display: "none" }}
                  >
                    DC
                  </span>
                  <span className="ml-2 font-bold text-gray-800">MoizWP</span>
                </a>
                <motion.button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  whileTap={{ scale: 0.9 }}
                >
                  <svg
                    className="w-6 h-6 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </motion.button>
              </div>

              {/* Mobile Menu Items */}
              <div className="flex flex-col p-6 space-y-4 bg-white overflow-y-auto flex-1">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.submenu ? (
                      <div>
                        <motion.a
                          href={getHref(item.href)}
                          className={`w-full text-left py-3 px-4 rounded-lg transition-colors duration-200 font-medium flex justify-between items-center ${
                            item.submenu.some((subItem) =>
                              isActiveMenuItem(subItem.href),
                            )
                              ? "bg-indigo-50 text-indigo-600 border-l-4 border-indigo-500"
                              : "text-gray-800 hover:bg-gray-50"
                          }`}
                          onClick={(e) => {
                            if (
                              e.metaKey ||
                              e.ctrlKey ||
                              e.shiftKey ||
                              e.altKey ||
                              e.button === 1
                            ) {
                              return;
                            }
                            e.preventDefault();
                            setActiveDropdown(
                              activeDropdown === item.label ? null : item.label,
                            );
                          }}
                        >
                          <span>{item.label}</span>
                          <motion.span
                            animate={{
                              rotate: activeDropdown === item.label ? 180 : 0,
                            }}
                            transition={{ duration: 0.2 }}
                            className="text-sm"
                          >
                            ▼
                          </motion.span>
                        </motion.a>

                        <AnimatePresence>
                          {activeDropdown === item.label && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 pt-2 space-y-2 pb-2">
                                {item.submenu?.map((subItem) => (
                                  <motion.a
                                    key={subItem.id}
                                    href={getHref(subItem.href)}
                                    className={`block w-full text-left py-2 px-4 rounded-lg transition-colors duration-200 text-sm ${
                                      isActiveMenuItem(subItem.href)
                                        ? "bg-indigo-100 text-indigo-700 font-semibold border-l-2 border-indigo-500"
                                        : "text-gray-600 hover:bg-gray-50"
                                    }`}
                                    onClick={(e) =>
                                      handleNavClick(e, subItem.href)
                                    }
                                    whileHover={{ x: 5 }}
                                  >
                                    <div className="font-medium">
                                      {subItem.label}
                                    </div>
                                    {subItem.description && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {subItem.description}
                                      </div>
                                    )}
                                  </motion.a>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <motion.a
                        href={getHref(item.href)}
                        className={`block w-full text-left py-3 px-4 rounded-lg transition-colors duration-200 font-medium ${
                          isActiveMenuItem(item.href)
                            ? "bg-indigo-50 text-indigo-600 border-l-4 border-indigo-500"
                            : "text-gray-800 hover:bg-gray-50"
                        }`}
                        onClick={(e) => handleNavClick(e, item.href)}
                        whileHover={{ x: 5 }}
                      >
                        {item.label}
                      </motion.a>
                    )}
                  </motion.div>
                ))}

                {/* Mobile CTA Button */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navItems.length * 0.1 }}
                  className="pt-4 border-t border-gray-200 flex-shrink-0"
                >
                  <PulseButton
                    href="/contact"
                    onClick={(e) => handleNavClick(e as any, "/contact")}
                    variant="rounded-lg"
                    size="lg"
                    className="w-full"
                  >
                    Get a Quote
                  </PulseButton>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
